import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useLocalizedNavigation } from '../hooks/useLocalizedNavigation';
import { useAgent, useProperties } from '../hooks/useSanityData';
import { Button } from '../components/ui/button';
import { Star, Phone, Mail, Award, Home, MapPin, ArrowLeft, Loader2, MessageCircle } from 'lucide-react';
import PropertyCard from '../components/PropertyCard';

const AgentDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getTextInCurrentLanguage } = useLanguage();
  const { getLocalizedPath } = useLocalizedNavigation();

  const { agent, loading: agentLoading } = useAgent(id || '');
  const { properties, loading: propertiesLoading } = useProperties();

  if (agentLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('agent.notFound')}</h2>
          <Button onClick={() => navigate(getLocalizedPath('/'))} className="bg-blue-600 hover:bg-blue-700">
            {t('navigation.home')}
          </Button>
        </div>
      </div>
    );
  }

  const agentProperties = properties.filter(p => p.agentId === agent.id);

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Back Button */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-gray-700 hover:text-blue-600"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.back')}
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Agent Profile Header */}
        <div className="bg-white rounded-xl p-8 shadow-lg mb-8">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            {/* Photo */}
            <img
              src={agent.photo}
              alt={getTextInCurrentLanguage(agent.name)}
              className="w-32 h-32 rounded-full object-cover border-4 border-blue-100"
            />

            {/* Info */}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {getTextInCurrentLanguage(agent.name)}
              </h1>

              {/* Rating */}
              <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
                <div className="flex items-center gap-1">
                  <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  <span className="text-lg font-semibold text-gray-900">{agent.rating}</span>
                </div>
                <span className="text-gray-600">
                  ({agent.reviewCount} {t('agent.reviews')})
                </span>
              </div>

              {/* Stats */}
              <div className="flex items-center justify-center md:justify-start gap-6 mb-4 text-gray-600">
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  <span>{agent.yearsExperience} {t('agent.yearsExp')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Home className="h-5 w-5" />
                  <span>{agent.propertiesSold} {t('agent.sold')}</span>
                </div>
              </div>

              {/* Contact Buttons */}
              <div className="flex flex-wrap justify-center md:justify-start gap-3">
                <Button
                  onClick={() => window.location.href = `tel:${agent.phone}`}
                  variant="outline"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  {agent.phone}
                </Button>
                <Button
                  onClick={() => window.location.href = `mailto:${agent.email || 'info@realtypy.com'}`}
                  variant="outline"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  {agent.email}
                </Button>
                <Button
                  onClick={() => {
                    const cleanPhone = agent.phone.replace(/\D/g, '');
                    window.location.href = `https://wa.me/${cleanPhone}?text=Hola,%20como%20podemos%20ayudarte`;
                  }}
                  variant="outline"
                >
                  <MessageCircle className="h-4 w-4 mr-2 text-green-500" />
                  WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Bio */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('agent.about')}</h2>
              <p className="text-gray-700 leading-relaxed">{getTextInCurrentLanguage(agent.bio)}</p>
            </div>

            {/* Agent's Properties */}
            {!propertiesLoading && agentProperties.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('agent.listings')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {agentProperties.map(property => (
                    <Link key={property.id} to={getLocalizedPath(`/property/${property.id}`)}>
                      <PropertyCard property={property} />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Specialties */}
            {agent.specialties.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{t('agent.specialties')}</h3>
                <div className="flex flex-wrap gap-2">
                  {agent.specialties.map((specialty, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium"
                    >
                      {getTextInCurrentLanguage(specialty)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Languages */}
            {agent.languages.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{t('agent.languagesLabel')}</h3>
                <div className="flex flex-wrap gap-2">
                  {agent.languages.map((lang, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium"
                    >
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Service Areas */}
            {agent.serviceAreas.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{t('agent.serviceAreas')}</h3>
                <div className="space-y-2">
                  {agent.serviceAreas.map((area, index) => (
                    <div key={index} className="flex items-center gap-2 text-gray-700">
                      <MapPin className="h-4 w-4 text-blue-600" />
                      <span>{getTextInCurrentLanguage(area)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDetailPage;
